import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import apiClient from '../services/api';
import { clearSilentSsoFailedFlag, consumeSsoReturnTo } from '../utils/silentSso';

interface OrgOption {
  code: string;
  name: string;
}

/**
 * Choix d'organisation quand plusieurs abonnements inventwee-web.
 * Pas de mot de passe, pas de bouton de login.
 */
const SelectOrganizationPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiClient.get('/auth/sso/pending-organization');
        if (cancelled) return;
        setEmail(response.data?.email || '');
        setOrganizations(response.data?.organizations || []);
      } catch {
        if (!cancelled) {
          window.location.replace('/login?silent_sso=failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = async (orgCode: string) => {
    setSubmitting(orgCode);
    setError(null);
    try {
      const response = await apiClient.post('/auth/sso/select-organization', { orgCode });
      clearSilentSsoFailedFlag();
      const returnTo =
        (typeof response.data?.returnTo === 'string' && response.data.returnTo) ||
        consumeSsoReturnTo('/');
      window.location.replace(returnTo);
    } catch (err: any) {
      const payload = err?.response?.data;
      const message =
        payload?.message?.message ||
        (typeof payload?.message === 'string' ? payload.message : null) ||
        'Cette organisation n’est pas disponible.';
      setError(message);
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Choisir une organisation</h1>
        <p className="text-gray-600 mb-6">
          {email
            ? `${email} a accès à plusieurs organisations InventWee.`
            : 'Plusieurs organisations sont disponibles pour ce compte.'}
        </p>
        <div className="space-y-3">
          {organizations.map((org) => (
            <button
              key={org.code}
              type="button"
              disabled={submitting !== null}
              onClick={() => void choose(org.code)}
              className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors disabled:opacity-60"
            >
              <span className="block font-medium text-gray-900">{org.name}</span>
              <span className="block text-sm text-gray-500">{org.code}</span>
            </button>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default SelectOrganizationPage;
